"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { ButtonLink } from "@/components/ui/button-link";

interface HeroVideoProps {
  title: string;
  subtitle?: string;
  ctaText?: string;
  ctaHref?: string;
  badge?: string;
}

const HeroVideo = ({ title, subtitle, ctaText, ctaHref, badge }: HeroVideoProps) => {
  return (
    <section className="relative py-32 md:py-44 px-6 bg-foreground overflow-hidden">
      {/* Animated gradient background */}
      <div
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(ellipse at 20% 50%, hsl(var(--primary)) 0%, transparent 50%), radial-gradient(ellipse at 80% 50%, hsl(var(--secondary)) 0%, transparent 50%)",
          animation: "pulse 8s ease-in-out infinite alternate",
        }}
      />
      {/* Subtle grid pattern */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="container max-w-4xl mx-auto text-center relative z-10">
        {/* Badge */}
        {badge && (
          <BlurFade delay={0.05}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/10 mb-8">
              <span className="text-sm font-medium text-white/80">{badge}</span>
            </div>
          </BlurFade>
        )}

        {/* Title */}
        <BlurFade delay={0.1}>
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]">
            {title}
          </h1>
        </BlurFade>

        {/* Subtitle */}
        {subtitle && (
          <BlurFade delay={0.2}>
            <p className="mt-6 text-lg md:text-xl text-white/60 max-w-2xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          </BlurFade>
        )}

        {/* CTA */}
        {ctaText && ctaHref && (
          <BlurFade delay={0.3}>
            <div className="mt-10">
              <ButtonLink
                href={ctaHref}
                variant="primary"
                size="lg"
                className="font-bold text-lg px-10 py-6"
              >
                {ctaText}
              </ButtonLink>
            </div>
          </BlurFade>
        )}
      </div>

      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1) rotate(0deg); }
          100% { transform: scale(1.1) rotate(3deg); }
        }
      `}</style>
    </section>
  );
};

export default HeroVideo;
